import {
  PText,
  PFlexItem,
  PGrid,
  PGridItem,
  GridItemSize,
  PHeadline,
  PFlex,
  PLinkPure,
  PInlineNotification
} from '@porsche-design-system/components-react'
import parse, { HTMLReactParserOptions, Element, domToReact } from 'html-react-parser'
import React from 'react'
import { LightgalleryItem } from 'react-lightgallery'

import Content from '../../shared/components/Content/Content'
import ContentHeader from '../../shared/components/Content/Header/ContentHeader'
import ContentMain from '../../shared/components/Content/Main/ContentMain'
import Panel from '../../shared/components/Panel/Panel'

import styles from './howto.module.css'

const ENV_LABEL = process.env.REACT_APP_ENV_LABEL || ''

export type Section = {
  type: string
  index?: string
  imageSrc?: string
  width?: GridItemSize
  content: string
  contentName: string
}

export type PageContent = {
  title: string
  sections: Section[]
}

type Props = {
  data: PageContent
}

const HowTo = ({ data }: Props) => {
  const createTableOfContentsPanel = (section: Section) => {
    return (
      <PGridItem size={section.width} style={{ marginBottom: '2rem' }}>
        <Panel>
          <div className={styles.content}>
            <PHeadline variant='headline-4'>Inhaltsverzeichnis</PHeadline>
            {data.sections.map((val, index) => {
              if (val.type === 'rich text' && val.index) {
                const link = '#' + val.index
                return (
                  <PLinkPure key={index} href={link} style={{ marginBottom: '0.5rem', width: '100%' }}>
                    {val.contentName}
                  </PLinkPure>
                )
              }
            })}
          </div>
        </Panel>
      </PGridItem>
    )
  }

  const createTextOnlyPanel = (section: Section) => {
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (domNode instanceof Element && domNode.name === 'p') {
          return <PText>{domToReact(domNode.children, options)}</PText>
        }
        if (domNode instanceof Element && domNode.name === 'h1') {
          return <PHeadline variant='headline-1'>{domToReact(domNode.children, options)}</PHeadline>
        }
        if (domNode instanceof Element && domNode.name === 'h2') {
          return <PHeadline variant='headline-2'>{domToReact(domNode.children, options)}</PHeadline>
        }
        if (domNode instanceof Element && domNode.name === 'h3') {
          return <PHeadline variant='headline-3'>{domToReact(domNode.children, options)}</PHeadline>
        }
        if (domNode instanceof Element && domNode.name === 'h4') {
          return <PHeadline variant='headline-4'>{domToReact(domNode.children, options)}</PHeadline>
        }
        if (domNode instanceof Element && domNode.name === 'row') {
          return <PFlex className={styles.gap}>{domToReact(domNode.children, options)}</PFlex>
        }
        if (domNode instanceof Element && domNode.name === 'img') {
          return (
            <div className={styles.galleryItem}>
              <LightgalleryItem src={section.imageSrc}>
                <img src={section.imageSrc} className={styles.smallImage} alt='screenshot' />
              </LightgalleryItem>
            </div>
          )
        }
      }
    }

    const replacedText = parse(section.content, options)

    return (
      <PGridItem size={section.width} style={{ marginBottom: '2rem' }} id={section.index}>
        <Panel>
          <div className={styles.content}>{replacedText}</div>
        </Panel>
      </PGridItem>
    )
  }

  const createFullPicturePanel = (section: Section) => {
    return (
      <PGridItem size={section.width} style={{ marginBottom: '2rem' }} id={section.index}>
        <Panel>
          <div className={styles.imageWrapper}>
            <img src={section.imageSrc} alt='Header' />
          </div>
        </Panel>
      </PGridItem>
    )
  }

  return (
    <Content>
      <ContentHeader>
        <PFlexItem>
          <PText size='small'>How-To / {data.title}</PText>
        </PFlexItem>
      </ContentHeader>
      <ContentMain>
        {ENV_LABEL && (
          <>
            <PInlineNotification heading={ENV_LABEL} state='warning' persistent>
              Alle Angaben in diesem Teil beziehen sich auf die produktive Umgebung.
            </PInlineNotification>
            <br />
          </>
        )}
        <PGrid>
          {data.sections.map((section, i) => {
            return (
              <React.Fragment key={i}>
                {section.type === 'picture only' && createFullPicturePanel(section)}
                {section.type === 'rich text' && createTextOnlyPanel(section)}
                {section.type === 'table of contents' && createTableOfContentsPanel(section)}
              </React.Fragment>
            )
          })}
        </PGrid>
      </ContentMain>
    </Content>
  )
}

export default HowTo
